import axios, { type AxiosRequestConfig } from 'axios';
import { useContext, useMemo } from 'react';
import { config } from '../../../modules/config';
import { AuthContext } from '../../../modules/context/authContext';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type { TemporaryAccessCodeStatus } from '../../../modules/enums/status';
import type { AccessCodeKind } from '../../../modules/enums/types';
import type {
  AccessCodeRecord,
  CreateAccessCodePayload,
  UpdateAccessCodeTimeFramePayload,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

/**
 * Admin operations for the unified v3 access codes (`v3/locks/access-codes`).
 *
 * Same thin axios-backed shape as the lock hooks (Cognito token + `property-id`
 * header, unwraps `data.result`). The list redacts the PIN; `revealCode` hits the
 * dedicated PIN-reveal route. There is no status-toggle endpoint in v3 — a code is
 * removed (or extended) rather than disabled.
 */
export interface AccessCodesFilters {
  status?: TemporaryAccessCodeStatus;
  kind?: AccessCodeKind;
}

export interface AccessCodesService {
  list: (filters?: AccessCodesFilters) => Promise<AccessCodeRecord[]>;
  get: (uuid: string) => Promise<AccessCodeRecord>;
  revealCode: (uuid: string) => Promise<string>;
  create: (payload: CreateAccessCodePayload) => Promise<AccessCodeRecord>;
  updateTimeFrame: (
    uuid: string,
    payload: UpdateAccessCodeTimeFramePayload,
  ) => Promise<void>;
  remove: (uuid: string) => Promise<void>;
}

export const useAccessCodes = (): AccessCodesService => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const token = credentialsInfo?.token;
  const propertyHeaderId = selectedProperty?.property.uuid;

  return useMemo<AccessCodesService>(() => {
    const request = async <R>(reqConfig: AxiosRequestConfig): Promise<R> => {
      const headers: Record<string, string> = {};
      if (token) headers.authorization = token;
      if (propertyHeaderId) headers['property-id'] = propertyHeaderId;

      const { data } = await axios.request<SuccessEnvelop<R>>({
        baseURL: config.bookingEngine.endpoint,
        ...reqConfig,
        headers: { ...headers, ...reqConfig.headers },
      });
      return data.result as R;
    };

    return {
      list: (filters) =>
        request<AccessCodeRecord[]>({
          url: endpoints.ACCESS_CODES,
          method: 'get',
          params: filters,
        }),

      get: (uuid) =>
        request<AccessCodeRecord>({
          url: `${endpoints.ACCESS_CODES}/${uuid}`,
          method: 'get',
        }),

      revealCode: (uuid) =>
        request<{ accessCode: string }>({
          url: `${endpoints.ACCESS_CODES}/${uuid}/code`,
          method: 'get',
        }).then((result) => result.accessCode),

      create: (payload) =>
        request<AccessCodeRecord>({
          url: endpoints.ACCESS_CODES,
          method: 'post',
          data: payload,
        }),

      updateTimeFrame: (uuid, payload) =>
        request<void>({
          url: `${endpoints.ACCESS_CODES}/${uuid}/time-frame`,
          method: 'patch',
          data: payload,
        }),

      remove: (uuid) =>
        request<void>({
          url: `${endpoints.ACCESS_CODES}/${uuid}`,
          method: 'delete',
        }),
    };
  }, [token, propertyHeaderId]);
};
